import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon.jsx';
import { useT, useLang } from '../lib/i18n.jsx';
import { useAuth } from '../lib/auth.jsx';
import { get } from '../lib/api.js';
import { errorMessage } from '../lib/errors.js';
import { useAsync, initials } from '../lib/hooks.js';
import { formatDate, formatMoney, relativeTime } from '../lib/format.js';
import { hostingNeedsAttention, hostingState, isOpenInvoice, isOverdueInvoice } from '../lib/status.js';
import { useBackupSummaries, useResourceUsage } from '../lib/backups.js';

export default function Dashboard() {
  const t = useT();
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: hostings, loading: hostingsLoading, error: hostingsError } = useAsync(() => get('/hostings'), []);
  const { data: invoices } = useAsync(() => get('/invoices').catch(() => null), []);
  const { data: activity } = useAsync(() => get('/activity/me?limit=6').catch(() => []), []);
  const backups = useBackupSummaries(hostings);
  const usage = useResourceUsage(hostings);

  const sites = (hostings || []).map((h) => {
    const last = backups?.[h.id]?.lastCompletedAt;
    return {
      id: h.id,
      domain: h.domain,
      pkg: h.package?.name || '',
      state: hostingState(h, lang),
      fav: initials(h.domain.split('.')[0]),
      mail: h.modules?.email?.count || 0,
      backup: backups == null ? '…' : last ? relativeTime(last, lang) : '—',
      expires: h.next_billing_date ? formatDate(h.next_billing_date, lang) : '—',
    };
  });

  const attention = (hostings || []).filter(hostingNeedsAttention).length;
  const openInvoices = (invoices || []).filter(isOpenInvoice);
  const openTotal = openInvoices.reduce((a, i) => a + (i.total_amount || 0), 0);
  const anyOverdue = openInvoices.some(isOverdueInvoice);

  // Disk usage summed over hostings that have been sampled at least once.
  const diskSamples = Object.values(usage || {}).filter((u) => u && u.disk_limit_mb);
  const diskUsedGb = diskSamples.reduce((a, u) => a + (u.disk_usage_mb || 0), 0) / 1024;
  const diskTotalGb = diskSamples.reduce((a, u) => a + (u.disk_limit_mb || 0), 0) / 1024;

  const greeting = (() => {
    const hr = new Date().getHours();
    const name = (user?.full_name || user?.email || '').split(' ')[0];
    const part = hr < 6 ? 'night' : hr < 12 ? 'morning' : hr < 18 ? 'afternoon' : 'evening';
    return name ? t(`dashboard.greeting.${part}.named`, { name }) : t(`dashboard.greeting.${part}`);
  })();

  const actCards = (activity || []).slice(0, 6).map((a) => {
    const action = (a.action || '').toLowerCase();
    const isWarn = action.includes('fail') || action.includes('error') || action.includes('suspend');
    const isCreate = action.startsWith('create') || action.includes('register');
    const ico = isWarn ? 'warn' : isCreate ? 'acc' : 'ok';
    return {
      ico,
      text: activityText(a, t),
      when: a.created_at ? relativeTime(a.created_at, lang) : '',
    };
  });

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting}</h1>
          <p className="page-sub">{t('dash.subtitle')}</p>
        </div>
        {hostings && hostings.length > 0 && (
          attention > 0 ? (
            <div className="status-pill is-warn">
              <span className="dot" />
              {attention === 1 ? t('status.attention.1') : t('status.attention.n', { n: attention })}
            </div>
          ) : (
            <div className="status-pill">
              <span className="dot" />
              {t('status.allgood')}
            </div>
          )
        )}
      </div>

      {/* Quick actions */}
      <div className="quick-grid">
        <button className="quick-card" onClick={() => navigate('/domeinen?action=addDomain')}>
          <span className="qi"><Icon name="globe" size={20} /></span>
          <span className="qt">{t('qa.domain')}</span>
          <span className="qd">{t('qa.domain.d')}</span>
        </button>
        <button className="quick-card" onClick={() => navigate('/e-mail?action=newEntry')}>
          <span className="qi"><Icon name="mail" size={20} /></span>
          <span className="qt">{t('qa.mailbox')}</span>
          <span className="qd">{t('qa.mailbox.d')}</span>
        </button>
        <button className="quick-card" onClick={() => navigate('/domeinen?action=dns')}>
          <span className="qi"><Icon name="settings" size={20} /></span>
          <span className="qt">{t('qa.dns')}</span>
          <span className="qd">{t('qa.dns.d')}</span>
        </button>
        <button className="quick-card" onClick={() => navigate('/backups')}>
          <span className="qi"><Icon name="rotate" size={20} /></span>
          <span className="qt">{t('qa.restore')}</span>
          <span className="qd">{t('qa.restore.d')}</span>
        </button>
      </div>

      {/* Banner: only for something the customer can act on */}
      {openInvoices.length > 0 && (
        <div className="banner">
          <div>
            <h2>{openInvoices.length === 1 ? t('next.invoice.title') : t('next.invoices.title')}</h2>
            <p>{t(anyOverdue ? 'next.overdue.body' : 'next.invoice.body', { amount: formatMoney(openTotal, lang) })}</p>
          </div>
          <button className="btn-ghost" style={{ padding: '8px 14px' }} onClick={() => navigate('/facturatie')}>
            {t('next.invoice.cta')} →
          </button>
        </div>
      )}

      {/* Main grid */}
      <div className="dash-grid">
        {/* Services list */}
        <section className="card">
          <div className="card-h">
            <h3>{t('svc.title')}</h3>
            <button className="more" onClick={() => navigate('/hosting')}>{t('svc.viewall')} →</button>
          </div>
          {hostingsLoading && (
            <div className="state-card" style={{ borderRadius: 0, border: 0 }}>
              {t('common.loading')}
            </div>
          )}
          {!hostingsLoading && hostingsError && (
            <div className="state-card" style={{ borderRadius: 0, border: 0 }} role="alert">
              <h3>{t('dashboard.sites.error')}</h3>
              <p>{errorMessage(hostingsError, { lang })}</p>
            </div>
          )}
          {!hostingsLoading && !hostingsError && sites.length === 0 && (
            <div className="state-card" style={{ borderRadius: 0, border: 0 }}>
              <h3>{t('dashboard.sites.empty')}</h3>
              <p>{t('dashboard.sites.empty.body')}</p>
            </div>
          )}
          {sites.map((s) => (
            <div className="svc" key={s.id}>
              <div className="svc-name">
                <div className="fav">{s.fav}</div>
                <div className="nm">
                  <div className="domain">{s.domain}</div>
                  <div className="pkg">{s.pkg}{s.mail ? ` · ${t(s.mail === 1 ? 'dashboard.mailboxCount.one' : 'dashboard.mailboxCount.other', { n: s.mail })}` : ''}</div>
                </div>
              </div>
              <div className="svc-meta">
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 2 }}>{t('svc.backup')}</div>
                  <b>{s.backup}</b>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 2 }}>{t('svc.expires')}</div>
                  <b>{s.expires}</b>
                </div>
                <div style={{ alignSelf: 'center' }}>
                  <span className={`status-pill ${s.state.tone}`.trim()} title={s.state.message || undefined}>
                    <span className="dot" />
                    {s.state.label}
                  </span>
                </div>
              </div>
              <button className="btn-ghost" onClick={() => navigate(`/hosting/${s.id}`)}>
                {t('svc.manage')} →
              </button>
            </div>
          ))}
        </section>

        {/* Side stack */}
        <aside className="side-stack">
          <section className="card">
            <div className="card-h"><h3>{t('use.title')}</h3></div>
            <UsageRow
              label={t('use.disk')}
              used={Math.round(diskUsedGb * 10) / 10}
              total={Math.round(diskTotalGb * 10) / 10}
              unit="GB"
              empty={usage == null && hostings?.length ? '…' : t('dashboard.usage.none')}
            />
          </section>

          <section className="card">
            <div className="card-h">
              <h3>{t('act.title')}</h3>
            </div>
            {actCards.length === 0 && (
              <div style={{ padding: '14px 22px', fontSize: 13, color: 'var(--ink-3)' }}>
                {t('dashboard.activity.empty')}
              </div>
            )}
            {actCards.map((a, i) => (
              <div className="act" key={i}>
                <span className={`ico ${a.ico}`}>
                  {a.ico === 'ok' && <Icon name="check" size={13} stroke={2} />}
                  {a.ico === 'warn' && <Icon name="alert" size={13} stroke={1.8} />}
                  {a.ico === 'acc' && <Icon name="plus" size={13} stroke={2} />}
                </span>
                <span className="t">{a.text}</span>
                <span className="when">{a.when}</span>
              </div>
            ))}
          </section>
        </aside>
      </div>
    </main>
  );
}

function UsageRow({ label, used, total, unit, empty, warn }) {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const isWarn = warn || pct > 80;
  return (
    <div className="usage-row">
      <div className="top">
        <span className="lbl">{label}</span>
        {total > 0 ? (
          <span className="val">
            <b>{used}</b> / {total} {unit} <span style={{ marginLeft: 6, color: 'var(--ink-4)' }}>{pct}%</span>
          </span>
        ) : (
          <span className="val" style={{ color: 'var(--ink-4)' }}>{empty}</span>
        )}
      </div>
      <div className={`bar ${isWarn ? 'is-warn' : ''}`}>
        <i style={{ width: pct + '%' }} />
      </div>
    </div>
  );
}

// Activity log actions are free-form ("create_hosting", "restore_backup",
// "hosting_unhealthy"); translate the verb and resource we know, fall back to
// the raw action otherwise.
const ACTIVITY_VERBS = ['create', 'update', 'delete', 'restore', 'suspend', 'unsuspend', 'register', 'cancel', 'renew', 'pay'];
const ACTIVITY_RESOURCES = ['hosting', 'backup', 'domain', 'dns', 'email', 'invoice', 'payment', 'user', 'server', 'package'];

function activityText(a, t) {
  const words = String(a.action || '').toLowerCase().split(/[_.\s]+/).filter(Boolean);
  const verb = ACTIVITY_VERBS.find((v) => words.includes(v) || words.includes(`${v}d`) || words.includes(`${v}ed`));
  const type = String(a.resource_type || ACTIVITY_RESOURCES.find((r) => words.includes(r)) || '').toLowerCase();
  const resource = ACTIVITY_RESOURCES.includes(type) ? t(`dashboard.activity.resource.${type}`) : type;
  const label = a.resource_id ? `${resource} #${a.resource_id}` : resource;
  if (verb && resource) return t(`dashboard.activity.verb.${verb}`, { resource: label });
  const raw = words.join(' ');
  return label ? `${raw} · ${label}` : raw;
}
